import React, {useEffect} from "react";
import {
  ABreadcrumb,
  ACol,
  AContainer,
  APageLoader,
  ARow
} from "@cisco-sbg-ui/magna-react";
import {appConfig} from "config/defaultConfig";
import {t} from "i18next";
import {useDispatch, useSelector} from "react-redux";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import NewInvestigation from "features/investigate/NewInvestigationPanel";
import SavedInvestigationsPanel from "features/investigate/SavedInvestigationsPanel";
import {InvestigationDetailProvider} from "contexts/InvestigationDetailContext";
import {createInvestigation} from "store/features/investigationSlice";
import {selectInvestigationLoading} from "store/selectors/investigationSelectors";
import {getObservablesFromCase} from "api/casebook";
import InvestigationDetail from "./InvestigationDetail";
import css from "./Investigate.module.scss";

const {ROUTES} = appConfig.APP;

const Investigate = () => {
  const {id} = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {search} = useLocation();
  const searchQuery = new URLSearchParams(search);
  const observableArr = searchQuery.get("observables");
  const caseId = searchQuery.get("cbid") || searchQuery.get("spid");

  const loading = useSelector(selectInvestigationLoading);

  useEffect(() => {
    // If a URL Param "observables" exists, run a new investigation immediately using those observables,
    // instead of presenting the UI to the user.
    // Initiating flow this will route to the new investigation on success.
    if (search && observableArr?.length) {
      dispatch(createInvestigation({query: observableArr, navigate}));
    }
  }, [dispatch, search, observableArr]);

  useEffect(() => {
    // If a URL Param "cbid" or "spid" exists, run a new investigation immediately using the observables
    // from the associated case, instead of presenting the UI to the user.
    // Initiating flow this will route to the new investigation on success.
    if (!caseId) {
      return;
    }

    getObservablesFromCase(caseId).then((observables) => {
      if (search && observables?.length) {
        // Map to a queryable string
        const mappedObservables = observables
          .map(({type, value}) => {
            return `${type}:"${value}"`;
          })
          .join("\n");

        dispatch(createInvestigation({query: mappedObservables, navigate}));
      }
    });
  }, [dispatch, search, caseId]);

  if (loading) {
    // Show only page loader, is true while investigation is creating.
    // Router will forward to Investigation page once ready.
    // (This will be short, intended to prevent flash of content.)
    return (
      <div className={css.loader}>
        <APageLoader className={css.loaderSpinner} />
      </div>
    );
  }

  return (
    <AContainer fluid data-testid={"Investigate-Container"}>
      {id && (
        <ARow>
          <ACol>
            <ABreadcrumb
              className="px-0"
              items={[
                {
                  component: Link,
                  content: t("investigate.title"),
                  contentProps: {
                    to: ROUTES.INVESTIGATE
                  }
                }
              ]}
            />
          </ACol>
        </ARow>
      )}
      {!id ? (
        <ARow noGutters>
          <ACol xs="12">
            <NewInvestigation />
            <SavedInvestigationsPanel />
          </ACol>
        </ARow>
      ) : (
        <InvestigationDetailProvider>
          <InvestigationDetail {...{id}} />
        </InvestigationDetailProvider>
      )}
    </AContainer>
  );
};

export default Investigate;
